/**
 * Converts between frontend and backend condition formats.
 *
 * Frontend format (used by ConditionBuilder):
 * { compare: { left: { sensor: "temp" }, op: "gt", right: { literal: 20 } } }
 * { all: [ ... ] }
 * { any: [ ... ] }
 * { not: { ... } }
 * { timeBetween: { start: "09:00", end: "17:00" } }
 *
 * Backend format (expected by API):
 * { type: "compare", op: "gt", left: { type: "sensor", ref: "temp" }, right: { type: "literal", value: 20 } }
 * { type: "all", children: [ ... ] }
 * { type: "any", children: [ ... ] }
 * { type: "not", child: { ... } }
 * { type: "timeBetween", start: "09:00", end: "17:00" }
 */

import type { ConditionNode, Operand } from './conditionUtils';

// Backend types
interface BackendCondition {
  version: number;
  root: BackendConditionNode;
}

interface BackendConditionNode {
  type: 'all' | 'any' | 'not' | 'compare' | 'timeBetween';
  children?: BackendConditionNode[];
  child?: BackendConditionNode;
  op?: string;
  left?: BackendNumericExpr;
  right?: BackendNumericExpr;
  hysteresis?: {
    release?: BackendNumericExpr;
  };
  start?: string;
  end?: string;
}

interface BackendNumericExpr {
  type: 'literal' | 'sensor' | 'add' | 'sub' | 'mul' | 'div';
  value?: number;
  ref?: string;
  left?: BackendNumericExpr;
  right?: BackendNumericExpr;
}

/**
 * Convert frontend ConditionNode to backend format for API submission
 */
export function toBackendCondition(
  node: ConditionNode | null
): Record<string, unknown> | undefined {
  if (!node) return undefined;
  return {
    version: 1,
    root: convertNodeToBackend(node),
  };
}

function convertNodeToBackend(node: ConditionNode): BackendConditionNode {
  if (node.all) {
    return {
      type: 'all',
      children: node.all.map(convertNodeToBackend),
    };
  }

  if (node.any) {
    return {
      type: 'any',
      children: node.any.map(convertNodeToBackend),
    };
  }

  if (node.not) {
    return {
      type: 'not',
      child: convertNodeToBackend(node.not),
    };
  }

  if (node.compare) {
    const hysteresis = node.compare.hysteresis
      ? {
          release: { type: 'literal' as const, value: node.compare.hysteresis.release },
        }
      : undefined;
    return {
      type: 'compare',
      op: node.compare.op,
      left: convertOperandToBackend(node.compare.left),
      right: convertOperandToBackend(node.compare.right),
      hysteresis,
    };
  }

  if (node.timeBetween) {
    return {
      type: 'timeBetween',
      start: node.timeBetween.start,
      end: node.timeBetween.end,
    };
  }

  // Fallback - shouldn't happen with valid input
  throw new Error('Invalid condition node: no recognized type');
}

function convertOperandToBackend(operand: Operand): BackendNumericExpr {
  if ('sensor' in operand) {
    return { type: 'sensor', ref: operand.sensor };
  }

  if ('literal' in operand) {
    return { type: 'literal', value: operand.literal };
  }

  if ('add' in operand) {
    return {
      type: 'add',
      left: convertOperandToBackend(operand.add[0]),
      right: convertOperandToBackend(operand.add[1]),
    };
  }

  if ('sub' in operand) {
    return {
      type: 'sub',
      left: convertOperandToBackend(operand.sub[0]),
      right: convertOperandToBackend(operand.sub[1]),
    };
  }

  throw new Error('Invalid operand: no recognized type');
}

/**
 * Convert backend condition format to frontend ConditionNode
 */
export function fromBackendCondition(data: unknown): ConditionNode | null {
  if (!data || typeof data !== 'object') return null;

  const condition = data as BackendCondition;
  if (condition.version !== 1 || !condition.root) {
    // Try treating it as already-unwrapped (shouldn't happen but be safe)
    if ('type' in (data as object)) {
      return convertNodeFromBackend(data as BackendConditionNode);
    }
    return null;
  }

  return convertNodeFromBackend(condition.root);
}

function convertNodeFromBackend(node: BackendConditionNode): ConditionNode {
  switch (node.type) {
    case 'all':
      return {
        all: (node.children ?? []).map(convertNodeFromBackend),
      };

    case 'any':
      return {
        any: (node.children ?? []).map(convertNodeFromBackend),
      };

    case 'not':
      if (!node.child) throw new Error('Invalid not node: missing child');
      return {
        not: convertNodeFromBackend(node.child),
      };

    case 'compare':
      if (!node.left || !node.right) {
        throw new Error('Invalid compare node: missing left or right');
      }
      const release = node.hysteresis?.release;
      const hysteresis =
        release && release.type === 'literal' && release.value !== undefined
          ? { release: release.value }
          : undefined;
      return {
        compare: {
          left: convertOperandFromBackend(node.left),
          op: node.op as 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte',
          right: convertOperandFromBackend(node.right),
          hysteresis,
        },
      };

    case 'timeBetween':
      return {
        timeBetween: {
          start: node.start ?? '',
          end: node.end ?? '',
        },
      };

    default:
      throw new Error(`Unknown node type: ${node.type}`);
  }
}

function convertOperandFromBackend(expr: BackendNumericExpr): Operand {
  switch (expr.type) {
    case 'sensor':
      return { sensor: expr.ref ?? '' };

    case 'literal':
      return { literal: expr.value ?? 0 };

    case 'add':
      if (!expr.left || !expr.right) {
        throw new Error('Invalid add expression: missing operands');
      }
      return {
        add: [convertOperandFromBackend(expr.left), convertOperandFromBackend(expr.right)],
      };

    case 'sub':
      if (!expr.left || !expr.right) {
        throw new Error('Invalid sub expression: missing operands');
      }
      return {
        sub: [convertOperandFromBackend(expr.left), convertOperandFromBackend(expr.right)],
      };

    case 'mul':
    case 'div':
      // Frontend doesn't support mul/div yet, convert to literal 0 as fallback
      return { literal: 0 };

    default:
      throw new Error(`Unknown expression type: ${expr.type}`);
  }
}
